// Importar negocios desde Excel con mapeo manual de columnas, aviso de duplicados
// (se informan pero NO se cargan) y borrado masivo de importaciones por lote.
import { useState, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { Loader2, Upload, FileSpreadsheet, CheckCircle2, AlertTriangle, Trash2, History, RefreshCw, FileDown } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../App";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BASE_TARGETS = [
  { key: "title", label: "Nombre del negocio", required: true },
  { key: "stage", label: "Etapa" },
  { key: "close", label: "Fecha de cierre" },
  { key: "owner", label: "Propietario" },
  { key: "value", label: "Valor" },
  { key: "external_id", label: "ID de registro (evita duplicados)" },
];

export default function ImportCardsDialog({ pipeline, onClose, onImported }) {
  const { getAuthHeaders } = useAuth();
  const [fileB64, setFileB64] = useState(null);
  const [fileName, setFileName] = useState("");
  const [preview, setPreview] = useState(null);
  const [columnMap, setColumnMap] = useState({});
  const [defaultStageId, setDefaultStageId] = useState(pipeline.stages?.[0]?.id || "");
  const [working, setWorking] = useState(false);
  const [done, setDone] = useState(null);
  const [batches, setBatches] = useState([]);
  const [showDups, setShowDups] = useState(false);
  const [updateExisting, setUpdateExisting] = useState(false);
  const [ignoreRequired, setIgnoreRequired] = useState(false);
  const [newFieldsSel, setNewFieldsSel] = useState({}); // {colIndex: false} => desmarcado
  const [updSel, setUpdSel] = useState({}); // {card_id: true} => negocios existentes a actualizar con el archivo

  // Columnas del archivo con datos que no están mapeadas a ningún campo: candidatas a crearse
  const mappedCols = new Set(Object.values(columnMap).filter((v) => v !== "" && v !== null && v !== undefined).map(Number));
  const newFieldCandidates = (preview?.headers || [])
    .map((h, i) => ({ i, h }))
    .filter(({ i, h }) => h && !mappedCols.has(i) && (preview?.col_filled?.[i] || 0) > 0);
  const checkedNewFields = newFieldCandidates.filter(({ i }) => newFieldsSel[i] === true);

  const targets = [
    ...BASE_TARGETS,
    ...(pipeline.fields || []).map((f) => ({ key: `cf_${f.name}`, label: `${f.label} (campo del pipeline)` })),
  ];

  const fetchBatches = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/pipelines/${pipeline.id}/import-batches`, getAuthHeaders());
      if (r.ok) { const d = await r.json(); setBatches(d.items || []); }
    } catch { /* silencioso */ }
  }, [pipeline.id, getAuthHeaders]);

  useEffect(() => { fetchBatches(); }, [fetchBatches]);

  const callImport = async (b64, dryRun, cmap, updExisting = updateExisting, createFields = null, updateSelected = null, ignoreReq = ignoreRequired) => {
    const body = { data_b64: b64, dry_run: dryRun, default_stage_id: defaultStageId || null, update_existing: !!updExisting, ignore_required: !!ignoreReq };
    if (createFields && createFields.length) body.create_fields = createFields;
    if (updateSelected && updateSelected.length) body.update_selected = updateSelected;
    if (cmap) {
      const clean = Object.fromEntries(Object.entries(cmap).filter(([, v]) => v !== "" && v !== null && v !== undefined));
      body.column_map = clean;
    }
    const r = await fetch(`${API_URL}/api/pipelines/${pipeline.id}/import-cards`, {
      method: "POST",
      headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!r.ok) {
      const e = await r.json();
      throw new Error(typeof e.detail === "string" ? e.detail : "Error al procesar el archivo");
    }
    return r.json();
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("El archivo excede 10 MB"); return; }
    setWorking(true);
    setPreview(null);
    setDone(null);
    try {
      const b64 = await new Promise((res, rej) => {
        const rd = new FileReader();
        rd.onload = () => res(rd.result.split(",")[1]);
        rd.onerror = rej;
        rd.readAsDataURL(file);
      });
      setFileB64(b64);
      setFileName(file.name);
      setUpdSel({});
      setNewFieldsSel({});
      const d = await callImport(b64, true, null);
      setPreview(d);
      setColumnMap(d.auto_map || {});
    } catch (err) { toast.error(err.message || "Error al leer el archivo"); }
    setWorking(false);
    e.target.value = "";
  };

  const refreshPreview = async () => {
    if (!fileB64) return;
    if (columnMap.title === "" || columnMap.title === undefined || columnMap.title === null) {
      toast.error("Debés mapear la columna del Nombre del negocio"); return;
    }
    setWorking(true);
    try {
      const d = await callImport(fileB64, true, columnMap);
      setPreview((p) => ({ ...d, headers: p?.headers || d.headers }));
      toast.success("Vista previa actualizada");
    } catch (err) { toast.error(err.message); }
    setWorking(false);
  };

  const toggleUpdateExisting = async (checked) => {
    setUpdateExisting(checked);
    if (!fileB64) return;
    setWorking(true);
    try {
      const d = await callImport(fileB64, true, columnMap, checked);
      setPreview((p) => ({ ...d, headers: p?.headers || d.headers }));
    } catch (err) { toast.error(err.message); }
    setWorking(false);
  };

  const toggleIgnoreRequired = async (checked) => {
    setIgnoreRequired(checked);
    if (!fileB64) return;
    setWorking(true);
    try {
      const d = await callImport(fileB64, true, columnMap, updateExisting, null, null, checked);
      setPreview((p) => ({ ...d, headers: p?.headers || d.headers }));
    } catch (err) { toast.error(err.message); }
    setWorking(false);
  };

  const handleImport = async () => {
    setWorking(true);
    try {
      const d = await callImport(fileB64, false, columnMap, updateExisting,
        checkedNewFields.map(({ i, h }) => ({ col: i, label: h })),
        Object.keys(updSel).filter((k) => updSel[k]));
      setDone(d);
      const extra = [];
      if (d.campos_creados) extra.push(`${d.campos_creados} campo(s) creados`);
      if (d.actualizados_seleccionados) extra.push(`${d.actualizados_seleccionados} negocio(s) actualizados`);
      toast.success(`${d.creados} negocio(s) importados${extra.length ? ` · ${extra.join(" · ")}` : ""}`);
      fetchBatches();
      onImported?.();
    } catch (err) { toast.error(err.message || "Error al importar"); }
    setWorking(false);
  };

  const downloadPlantilla = async () => {
    try {
      const r = await fetch(`${API_URL}/api/pipelines/${pipeline.id}/plantilla-import`, getAuthHeaders());
      if (!r.ok) throw new Error("No se pudo generar la plantilla");
      const blob = await r.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `plantilla_${pipeline.name.replace(/\s+/g, "_")}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Plantilla descargada con los campos y etapas actualizados");
    } catch (err) { toast.error(err.message || "Error al descargar la plantilla"); }
  };

  const handleDeleteBatch = async (b) => {
    if (!window.confirm(`¿Borrar los ${b.count} negocios creados por esta importación? Esta acción no se puede deshacer.`)) return;    try {
      const r = await fetch(`${API_URL}/api/pipelines/${pipeline.id}/import-batches/${b.batch_id}`, {
        method: "DELETE", headers: getAuthHeaders().headers,
      });
      if (!r.ok) { const e = await r.json(); throw new Error(typeof e.detail === "string" ? e.detail : "Error"); }
      const d = await r.json();
      toast.success(`${d.eliminados} negocios eliminados`);
      fetchBatches();
      onImported?.();
    } catch (err) { toast.error(err.message || "Error al borrar la importación"); }
  };

  const etapasNo = Object.entries(preview?.etapas_no_encontradas || {});
  const propNo = Object.entries(preview?.propietarios_no_encontrados || {});
  const dups = preview?.duplicados_detalle || [];
  const difs = preview?.diferentes_detalle || [];
  const selCount = Object.values(updSel).filter(Boolean).length;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto"
        onInteractOutside={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        data-testid="import-cards-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-[#80BC00]" /> Importar negocios · {pipeline.name}
          </DialogTitle>
        </DialogHeader>

        {done ? (
          <div className="text-center py-4 space-y-2" data-testid="import-result">
            <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto" />
            <p className="text-sm font-bold text-slate-800">{done.creados} negocios importados</p>
            {done.actualizados > 0 && (
              <p className="text-xs text-emerald-700" data-testid="import-result-updated">{done.actualizados} negocio(s) existentes completados con campos vacíos.</p>
            )}
            {done.actualizados_seleccionados > 0 && (
              <p className="text-xs text-emerald-700" data-testid="import-result-updated-sel">{done.actualizados_seleccionados} negocio(s) existentes actualizados con los datos del archivo.</p>
            )}
            {done.duplicados > 0 && (
              <p className="text-xs text-slate-500">{done.duplicados} negocio(s) NO se cargaron por ser posibles duplicados.</p>
            )}
            <Button onClick={onClose} className="bg-[#80BC00] hover:bg-[#6ea700] text-white mt-2" data-testid="import-close-btn">Cerrar</Button>
          </div>
        ) : (
          <>
            <p className="text-xs text-slate-600">
              Subí el Excel exportado de la otra plataforma. El sistema detecta las columnas automáticamente y
              podés <b>ajustar el mapeo manualmente</b>, incluyendo los campos personalizados del pipeline.
              Los negocios duplicados <b>no se cargan</b>: solo se informan.
            </p>
            <button
              type="button"
              onClick={downloadPlantilla}
              className="flex items-center gap-1.5 text-xs text-[#193966] font-semibold hover:underline"
              data-testid="import-download-plantilla"
            >
              <FileDown className="w-3.5 h-3.5 text-[#80BC00]" /> Descargar plantilla actualizada de este pipeline (campos y etapas al día)
            </button>
            <label className="block">
              <input type="file" accept=".xlsx" onChange={handleFile} disabled={working} className="hidden" data-testid="import-file-input" />
              <span className="inline-flex items-center gap-2 px-3 py-2 bg-[#193966] hover:bg-[#142d4f] text-white text-sm rounded cursor-pointer">
                {working && !preview ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {fileName ? `Cambiar archivo (${fileName})` : "Elegir archivo .xlsx"}
              </span>
            </label>

            {preview && (
              <div className="space-y-3" data-testid="import-preview">
                {preview.needs_mapping && (
                  <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 flex items-center gap-1" data-testid="import-needs-mapping">
                    <AlertTriangle className="w-3 h-3" /> No se pudo detectar automáticamente la columna del nombre del negocio. Mapeala abajo y presioná "Actualizar vista previa".
                  </p>
                )}
                {/* MAPEO DE COLUMNAS */}
                <div className="border rounded p-2.5 bg-slate-50" data-testid="import-mapping">
                  <p className="text-[10px] uppercase tracking-wider font-bold text-slate-600 mb-1.5">Mapeo de columnas</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1.5">
                    {targets.map((t) => (
                      <div key={t.key} className="flex items-center gap-1.5">
                        <span className="text-[11px] text-slate-700 w-[45%] leading-tight" title={t.label}>
                          {t.label}{t.required && <span className="text-rose-500"> *</span>}
                        </span>
                        <select
                          value={columnMap[t.key] ?? ""}
                          onChange={(e) => setColumnMap({ ...columnMap, [t.key]: e.target.value === "" ? "" : Number(e.target.value) })}
                          className="flex-1 h-7 border border-slate-300 rounded px-1 text-[11px] bg-white"
                          data-testid={`import-map-${t.key}`}
                        >
                          <option value="">— No importar —</option>
                          {(preview.headers || []).map((h, i) => <option key={i} value={i}>{h || `Columna ${i + 1}`}</option>)}
                        </select>
                      </div>
                    ))}
                  </div>
                  <Button size="sm" variant="outline" onClick={refreshPreview} disabled={working} className="h-7 mt-2 text-[11px]" data-testid="import-refresh-preview">
                    {working ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />} Actualizar vista previa
                  </Button>
                </div>

                {/* CONTADORES */}
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="border rounded p-2 bg-slate-50">
                    <p className="text-lg font-bold text-[#193966]" data-testid="import-total">{preview.total}</p>
                    <p className="text-[10px] uppercase text-slate-500">Filas leídas</p>
                  </div>
                  <div className="border rounded p-2 bg-[#80BC00]/5 border-[#80BC00]/40">
                    <p className="text-lg font-bold text-[#5f8f00]" data-testid="import-listos">{preview.listos}</p>
                    <p className="text-[10px] uppercase text-slate-500">Se importarán</p>
                  </div>
                  <div className="border rounded p-2 bg-rose-50 border-rose-200">
                    <p className="text-lg font-bold text-rose-600" data-testid="import-duplicados">{preview.duplicados}</p>
                    <p className="text-[10px] uppercase text-slate-500">Duplicados (no se cargan)</p>
                  </div>
                </div>

                {/* ACTUALIZAR EXISTENTES (opt-in) */}
                <label className="flex items-start gap-2 text-[11px] border rounded px-2 py-1.5 bg-emerald-50/50 border-emerald-200 cursor-pointer" data-testid="import-update-existing-label">
                  <input
                    type="checkbox"
                    checked={updateExisting}
                    onChange={(e) => toggleUpdateExisting(e.target.checked)}
                    className="mt-0.5 w-3.5 h-3.5 accent-emerald-600"
                    data-testid="import-update-existing"
                  />
                  <span className="text-slate-700">
                    <b>Actualizar existentes:</b> completar los campos <b>vacíos</b> de los negocios ya cargados con los datos del archivo.
                    No los duplica ni sobrescribe datos que ya tengan valor.
                    {updateExisting && preview.a_actualizar > 0 && (
                      <span className="block text-emerald-700 font-semibold mt-0.5" data-testid="import-a-actualizar">
                        {preview.a_actualizar} negocio(s) existentes se completarán.
                      </span>
                    )}
                    {updateExisting && preview.a_actualizar === 0 && (
                      <span className="block text-slate-500 mt-0.5">No hay campos vacíos que completar con este archivo.</span>
                    )}
                  </span>
                </label>

                {/* EXISTENTES CON CAMBIOS: actualización selectiva */}
                {difs.length > 0 && (
                  <div className="border rounded bg-sky-50/60 border-sky-200 px-2.5 py-2" data-testid="import-diferentes">
                    <p className="text-[11px] font-semibold text-sky-900 flex items-center gap-1">
                      <RefreshCw className="w-3 h-3" /> {preview.diferentes} negocio(s) ya existen pero el archivo trae cambios — marcá cuáles actualizar
                    </p>
                    <p className="text-[10px] text-slate-500 mt-0.5 mb-1">
                      No son duplicados (no coinciden al 100%). Los que marques se actualizarán con los valores del archivo; los demás quedan como están.
                    </p>
                    <div className="flex gap-2 mb-1">
                      <button type="button" className="text-[10px] text-sky-700 underline" onClick={() => setUpdSel(Object.fromEntries(difs.map((d) => [d.card_id, true])))} data-testid="import-diferentes-all">Marcar todos</button>
                      <button type="button" className="text-[10px] text-slate-500 underline" onClick={() => setUpdSel({})} data-testid="import-diferentes-none">Ninguno</button>
                      {selCount > 0 && <span className="text-[10px] font-semibold text-sky-800">{selCount} seleccionado(s)</span>}
                    </div>
                    <div className="max-h-52 overflow-y-auto space-y-1">
                      {difs.map((d) => (
                        <label key={d.card_id} className="flex items-start gap-1.5 text-[11px] bg-white border border-sky-100 rounded px-1.5 py-1 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={updSel[d.card_id] === true}
                            onChange={(e) => setUpdSel((s) => ({ ...s, [d.card_id]: e.target.checked }))}
                            className="mt-0.5 w-3.5 h-3.5 accent-sky-600"
                            data-testid={`import-dif-${d.card_id}`}
                          />
                          <span className="leading-tight">
                            <b className="text-slate-800">{d.title}</b>
                            <span className="block text-slate-600">
                              {(d.diffs || []).slice(0, 4).map((df, j) => (
                                <span key={j} className="block">• {df.campo}: <span className="line-through text-slate-400">{df.antes}</span> → <span className="text-sky-800 font-medium">{df.ahora}</span></span>
                              ))}
                              {(d.diffs || []).length > 4 && <span className="text-slate-400">… y {(d.diffs || []).length - 4} cambio(s) más</span>}
                            </span>
                          </span>
                        </label>
                      ))}
                      {preview.diferentes > difs.length && <p className="text-[10px] text-slate-400">… y {preview.diferentes - difs.length} más</p>}
                    </div>
                  </div>
                )}

                {/* CREAR CAMPOS NUEVOS desde columnas sin mapear */}
                {newFieldCandidates.length > 0 && (
                  <details className="border rounded bg-indigo-50/40 border-indigo-200" data-testid="import-new-fields">
                    <summary className="text-[11px] px-2.5 py-2 cursor-pointer text-indigo-900 font-semibold select-none">
                      ➕ Crear campos personalizados en el pipeline desde columnas del archivo ({checkedNewFields.length} de {newFieldCandidates.length} seleccionadas)
                    </summary>
                    <div className="px-2.5 pb-2">
                      <p className="text-[10px] text-slate-500 mb-1">
                        Columnas con datos que no existen como campo. Marcá las que querés crear: se crearán al importar y sus datos se cargarán en cada negocio.
                      </p>
                      <div className="flex gap-2 mb-1">
                        <button type="button" className="text-[10px] text-indigo-700 underline" onClick={() => setNewFieldsSel(Object.fromEntries(newFieldCandidates.map(({ i }) => [i, true])))}>Marcar todas</button>
                        <button type="button" className="text-[10px] text-slate-500 underline" onClick={() => setNewFieldsSel({})}>Ninguna</button>
                      </div>
                      <div className="max-h-44 overflow-y-auto grid grid-cols-1 md:grid-cols-2 gap-x-3">
                        {newFieldCandidates.map(({ i, h }) => (
                          <label key={i} className="flex items-center gap-1.5 text-[11px] text-slate-700 py-0.5 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={newFieldsSel[i] === true}
                              onChange={(e) => setNewFieldsSel((s) => ({ ...s, [i]: e.target.checked }))}
                              className="w-3.5 h-3.5 accent-indigo-600"
                              data-testid={`import-newfield-${i}`}
                            />
                            <span className="leading-tight">{h} <span className="text-slate-400">({preview.col_filled?.[i]} con datos)</span></span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </details>
                )}

                {/* DUPLICADOS: solo informativos */}
                {dups.length > 0 && (
                  <div className="text-[11px] bg-rose-50 border border-rose-200 rounded px-2 py-1.5" data-testid="import-dups-detail">
                    <button onClick={() => setShowDups(!showDups)} className="flex items-center gap-1 font-semibold text-rose-700 w-full text-left">
                      <AlertTriangle className="w-3 h-3" /> {preview.duplicados} negocio(s) duplicados detectados — NO se cargarán, solo se informan {showDups ? "▲" : "▼"}
                    </button>
                    {showDups && (
                      <ul className="mt-1 max-h-36 overflow-y-auto space-y-0.5 text-rose-700">
                        {dups.map((d, i) => <li key={i}>• <b>{d.title}</b>: {d.motivo}</li>)}
                        {preview.duplicados > dups.length && <li className="text-rose-500">… y {preview.duplicados - dups.length} más</li>}
                      </ul>
                    )}
                  </div>
                )}

                {/* OBLIGATORIOS POR ETAPA: filas que no se cargarán */}
                {((preview?.obligatorios_detalle || []).length > 0 || (ignoreRequired && preview?.obligatorios === 0)) && (
                  <div className="text-[11px] bg-rose-50 border border-rose-200 rounded px-2 py-1.5" data-testid="import-obligatorios">
                    {preview.obligatorios > 0 ? (
                      <>
                        <p className="flex items-center gap-1 font-semibold text-rose-700">
                          <AlertTriangle className="w-3 h-3" /> {preview.obligatorios} fila(s) NO se cargarán: campos obligatorios vacíos en su etapa
                        </p>
                        <ul className="mt-1 max-h-36 overflow-y-auto space-y-0.5 text-rose-700">
                          {preview.obligatorios_detalle.map((d, i) => (
                            <li key={i}>• <b>{d.title}</b> (etapa "{d.etapa}"): falta {d.faltan.join(", ")}</li>
                          ))}
                          {preview.obligatorios > preview.obligatorios_detalle.length && <li className="text-rose-500">… y {preview.obligatorios - preview.obligatorios_detalle.length} más</li>}
                        </ul>
                      </>
                    ) : (
                      <p className="font-semibold text-emerald-700">Las filas con campos obligatorios vacíos se importarán igualmente.</p>
                    )}
                    <label className="flex items-start gap-1.5 mt-1.5 pt-1.5 border-t border-rose-200 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={ignoreRequired}
                        onChange={(e) => toggleIgnoreRequired(e.target.checked)}
                        className="mt-0.5 w-3.5 h-3.5 accent-rose-600"
                        data-testid="import-ignore-required"
                      />
                      <span className="text-slate-700">
                        <b>Importarlas de todos modos:</b> se cargarán aunque tengan campos obligatorios vacíos.
                        Podrás completarlos luego en cada negocio (se exigirán al cambiar de etapa).
                      </span>
                    </label>
                  </div>
                )}

                {preview.sin_titulo > 0 && (
                  <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                    {preview.sin_titulo} fila(s) sin nombre de negocio serán omitidas.
                  </p>
                )}
                {etapasNo.length > 0 && (
                  <div className="text-[11px] bg-amber-50 border border-amber-200 rounded px-2 py-1.5 space-y-1">
                    <p className="flex items-center gap-1 font-semibold text-amber-800"><AlertTriangle className="w-3 h-3" /> Etapas del archivo que no existen en este pipeline:</p>
                    <p className="text-amber-700">{etapasNo.map(([n, c]) => `${n} (${c})`).join(" · ")}</p>
                    <div>
                      <Label className="text-[10px]">Esas filas irán a la etapa:</Label>
                      <select value={defaultStageId} onChange={(e) => setDefaultStageId(e.target.value)} className="w-full h-8 border border-slate-300 rounded px-2 text-xs bg-white" data-testid="import-default-stage">
                        {(pipeline.stages || []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                      </select>
                    </div>
                  </div>
                )}
                {propNo.length > 0 && (
                  <p className="text-[11px] text-slate-600 bg-slate-50 border border-slate-200 rounded px-2 py-1.5">
                    Propietarios no encontrados (quedarán <b>sin asignar</b>): {propNo.map(([n, c]) => `${n} (${c})`).join(" · ")}
                  </p>
                )}
              </div>
            )}

            {/* IMPORTACIONES ANTERIORES: borrado masivo por lote */}
            {batches.length > 0 && (
              <details className="border rounded bg-slate-50" data-testid="import-batches-section">
                <summary className="text-xs px-2.5 py-2 cursor-pointer text-slate-700 font-semibold flex items-center gap-1.5 select-none">
                  <History className="w-3.5 h-3.5" /> Importaciones realizadas ({batches.length}) — borrar masivamente
                </summary>
                <div className="px-2.5 pb-2 space-y-1">
                  {batches.map((b) => (
                    <div key={b.batch_id} className="flex items-center justify-between gap-2 text-[11px] border rounded bg-white px-2 py-1.5" data-testid={`import-batch-${b.batch_id}`}>
                      <span className="text-slate-700">
                        <b>{b.count}</b> negocios · {b.imported_at ? new Date(b.imported_at).toLocaleString("es-DO") : "—"} · por {b.imported_by_name}
                      </span>
                      <Button size="sm" variant="outline" onClick={() => handleDeleteBatch(b)} className="h-7 text-[11px] text-rose-600 border-rose-200 hover:bg-rose-50" data-testid={`import-batch-delete-${b.batch_id}`}>
                        <Trash2 className="w-3 h-3 mr-1" /> Borrar importación
                      </Button>
                    </div>
                  ))}
                </div>
              </details>
            )}

            <DialogFooter className="pt-2">
              <Button variant="outline" onClick={onClose}>Cancelar</Button>
              <Button
                onClick={handleImport}
                disabled={!preview || working || (!preview.listos && !(updateExisting && preview.a_actualizar > 0) && selCount === 0)}
                className="bg-[#80BC00] hover:bg-[#6ea700] text-white"
                data-testid="import-confirm-btn"
              >
                {working && preview ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Upload className="w-4 h-4 mr-1.5" />}
                Importar {preview?.listos || 0} negocios{updateExisting && preview?.a_actualizar > 0 ? ` + completar ${preview.a_actualizar}` : ""}{selCount > 0 ? ` + actualizar ${selCount}` : ""}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
